import { useMemo, useState, useCallback } from 'react';
import { Form, message } from 'antd';
import { useCanCheck } from '@/ability/helpers';
import { useProjectDetail } from '@hooks/projects/detail/useProjectDetail';
import { useAddProjectContributors } from '@hooks/projects/useAddProjectContributors';
import { useRemoveProjectContributor } from './useRemoveProjectContributor';
import { useUsers } from '@hooks/users/useUsers';
import { useCreateUser } from '@hooks/users/useCreateUser';
import { useRoles } from '@hooks/rbac/useRoles';

const SAAS_USERS_CREATE = 'saas.users.create';

function getUserRoleLabelForInvite(user) {
    if (!user || typeof user !== 'object') return '—';
    if (Array.isArray(user.roles) && user.roles.length) {
        const names = user.roles.map((r) => (r && typeof r === 'object' ? r.name : r)).filter(Boolean);
        const joined = names.map((n) => String(n).trim()).filter(Boolean).join(', ');
        if (joined) {
            return joined
                .replace(/[_-]+/g, ' ')
                .replace(/\s+/g, ' ')
                .trim()
                .replace(/\b\w/g, (m) => m.toUpperCase());
        }
    }
    const value =
        (typeof user.role === 'string' || typeof user.role === 'number' ? user.role : undefined) ??
        user.role?.name ??
        user.roleName ??
        user.role?.roleName ??
        user.designation ??
        user.position ??
        user.jobTitle;
    const role = String(value || '').trim();
    if (!role) return '—';
    if (role.toLowerCase() === 'pm') return 'PM';
    return role
        .replace(/[_-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/\b\w/g, (m) => m.toUpperCase());
}

/**
 * Contributors tab: project contributors list, invite modal state, form, and submit handler.
 * Page only consumes this hook and renders; column config is built in the page with global components.
 */
export function useProjectContributors(projectId) {
    const can = useCanCheck();
    const canCreateUser = can(SAAS_USERS_CREATE);

    const { data: project } = useProjectDetail(projectId);
    const assignedUsers = project?.assignedUsers ?? [];

    const [inviteModalOpen, setInviteModalOpen] = useState(false);
    const [inviteForm] = Form.useForm();
    const [createUserModalOpen, setCreateUserModalOpen] = useState(false);
    const [createForm] = Form.useForm();

    const { addContributors, isPending: addContributorsPending } = useAddProjectContributors(projectId);
    const { removeContributor, isPending: removeContributorPending } = useRemoveProjectContributor(projectId);
    const createUserMutation = useCreateUser();
    const { data: roles = [] } = useRoles({ enabled: createUserModalOpen });
    const { data: usersData } = useUsers(
        { page: 1, limit: 100 },
        { enabled: !!projectId && (inviteModalOpen || createUserModalOpen) },
    );
    const usersList = usersData?.data ?? [];

    const openInviteModal = useCallback(() => setInviteModalOpen(true), []);
    const closeInviteModal = useCallback(() => {
        setInviteModalOpen(false);
        inviteForm.resetFields();
    }, [inviteForm]);

    const openCreateUserModal = useCallback(() => setCreateUserModalOpen(true), []);
    const closeCreateUserModal = useCallback(() => {
        setCreateUserModalOpen(false);
        createForm.resetFields();
    }, [createForm]);

    const roleSelectOptions = useMemo(() => roles.map((r) => ({ value: r.id, label: r.name })), [roles]);

    const handleCreateUserSubmit = useCallback(() => {
        createForm.validateFields().then((values) => {
            createUserMutation.mutate(
                {
                    email: values.email,
                    name: values.name,
                    roleId: values.roleId,
                    password: values.password,
                },
                {
                    onSuccess: () => {
                        message.success('User created successfully');
                        closeCreateUserModal();
                    },
                    onError: (err) => {
                        message.error(err?.response?.data?.message || 'Failed to create user');
                    },
                },
            );
        });
    }, [createForm, createUserMutation, closeCreateUserModal]);

    const inviteSelectOptions = useMemo(() => {
        return usersList
            .filter((u) => {
                const uid = u.id ?? u._id;
                return !assignedUsers.some((a) => String(a.id) === String(uid));
            })
            .map((u) => {
                const id = u.id ?? u._id;
                const displayName = (u.name && String(u.name).trim()) || u.email || String(id ?? '');
                const email = (u.email && String(u.email).trim()) || '';
                const roleLabel = getUserRoleLabelForInvite(u);
                const label = [displayName, email].filter(Boolean).join(' · ');
                return {
                    value: id,
                    label,
                    displayName,
                    email,
                    roleLabel,
                };
            });
    }, [usersList, assignedUsers]);

    const filterSelectOption = useCallback((input, option) => {
        const q = (input || '').trim().toLowerCase();
        if (!q) return true;
        const name = String(option?.displayName ?? '').toLowerCase();
        const email = String(option?.email ?? '').toLowerCase();
        const role = String(option?.roleLabel ?? '').toLowerCase();
        const legacy = String(option?.label ?? '').toLowerCase();
        return name.includes(q) || email.includes(q) || role.includes(q) || legacy.includes(q);
    }, []);

    const handleInviteSubmit = useCallback(() => {
        inviteForm.validateFields().then((values) => {
            addContributors({ userIds: values.userIds }, { onSuccess: () => closeInviteModal() });
        });
    }, [inviteForm, addContributors, closeInviteModal]);

    const tableDataSource = useMemo(() => assignedUsers.map((u) => ({ ...u, key: u.id })), [assignedUsers]);

    return {
        assignedUsers,
        contributorCountLabel: `${assignedUsers.length} member${assignedUsers.length !== 1 ? 's' : ''}`,
        inviteModalOpen,
        openInviteModal,
        closeInviteModal,
        inviteForm,
        inviteSelectOptions,
        filterSelectOption,
        addContributorsPending,
        handleInviteSubmit,
        tableDataSource,
        canCreateUser,
        createUserModalOpen,
        openCreateUserModal,
        closeCreateUserModal,
        createForm,
        roleSelectOptions,
        createUserPending: createUserMutation.isPending,
        handleCreateUserSubmit,
        removeContributor,
        removeContributorPending,
    };
}
